import { FilterDateSelectValue } from '../../../core/entities/filterDateSelect/filter-date-select-value';
import { StateValidator } from '../../../core/services/filter-state/filter-state.service';

/**
 * Interface representing the persisted filter state for the Task List.
 * This is what gets saved to and restored from localStorage.
 * 
 * Note: We store primitive/serializable values (codes, IDs) rather than
 * full objects to ensure proper JSON serialization and avoid stale data.
 */
export interface TaskListFilterState {
  /** Selected tab index: 0=Open, 1=Closed, 2=All */
  tabIndex: number;

  /** Task type codes */
  typeCodes: string[];

  /** Location IDs */
  locationIds: string[];

  /** Task state codes (COMPLETED, ASSIGNED, NEW, UNASSIGNED) */
  stateCodes: string[];

  /** Due date filter configuration */
  dueDate: FilterDateSelectValue | null;

  /** Completed date filter configuration */
  completedDate: FilterDateSelectValue | null;

  /** Sort configuration */
  sort: {
    key: string;
    direction?: 'asc' | 'desc';
  };

  /** Page size preference */
  pageSize: number;

  /** Displayed field identifiers (optional for backward compatibility) */
  displayedFields?: string[];

  /** Show metadata row toggle */
  showMetadata?: boolean;
}

/**
 * Default filter state - used when no persisted state is available
 */
export const DEFAULT_TASK_LIST_FILTER_STATE: TaskListFilterState = {
  tabIndex: 0,
  typeCodes: [],
  locationIds: [],
  stateCodes: [],
  dueDate: null,
  completedDate: null,
  sort: { key: 'type', direction: 'asc' },
  pageSize: 15,
  displayedFields: ['address', 'order', 'customer', 'resources'],
  showMetadata: true
};

/**
 * Type guard to validate restored filter state.
 * Returns false if the structure doesn't match, causing the service
 * to clear the stored state and return null.
 * 
 * This protects against schema changes - if we add/remove/rename fields,
 * old stored data will be safely discarded.
 */
export const isValidTaskListFilterState: StateValidator<TaskListFilterState> = (
  obj: unknown
): obj is TaskListFilterState => {
  // Must be an object
  if (!obj || typeof obj !== 'object') {
    return false;
  }

  const state = obj as Record<string, unknown>;

  // Validate required fields and their types
  if (typeof state.tabIndex !== 'number') return false;
  if (!Array.isArray(state.typeCodes)) return false;
  if (!Array.isArray(state.locationIds)) return false;
  if (!Array.isArray(state.stateCodes)) return false;
  if (typeof state.pageSize !== 'number') return false;

  // dueDate can be null or an object
  if (state.dueDate !== null && typeof state.dueDate !== 'object') return false;

  // completedDate can be null or an object
  if (state.completedDate !== null && typeof state.completedDate !== 'object') return false;

  // sort must be an object with a key property
  if (!state.sort || typeof state.sort !== 'object') return false;
  const sort = state.sort as Record<string, unknown>;
  if (typeof sort.key !== 'string') return false;

  // displayedFields is optional but must be an array if present
  if (state.displayedFields !== undefined && !Array.isArray(state.displayedFields)) return false;

  // showMetadata is optional but must be a boolean if present
  if (state.showMetadata !== undefined && typeof state.showMetadata !== 'boolean') return false;

  return true;
};
